import { Box, ButtonBase, Paper, Stack, styled } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import { Info } from '@sweep-io/sweep-design/dist/icons/Info';
import { CheckCircle } from '@sweep-io/sweep-design/dist/icons/CheckCircle';
import { Warning } from '@sweep-io/sweep-design/dist/icons/Warning';
import { Prohibit } from '@sweep-io/sweep-design/dist/icons/Prohibit';
import { Notification } from '@sweep-io/sweep-design/dist/icons/Notification';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { forwardRef } from 'react';

const InteractiveButton = styled(ButtonBase)({
  '&:hover, &:active': {
    '& .MuiTypography-root': {
      color: colors.grey[300],
    },
    '& svg': {
      color: colors.grey[300],
    },
  },
});

interface NotificationAlertProps {
  message: string;
  onClose?: () => void;
  severity?: SweepNotificationVariant;
  action?: {
    actionText: string;
    onActionClick: () => any;
  };
  details?: string;
  isVariant: boolean;
  linkUrl?: string;
  linkText?: string;
  linkOpenInNewTab?: boolean;
  onNavigate?: (url: string) => void;
  hideClose?: boolean;
}

const getBackgroundColor = (variant: SweepNotificationVariant) => {
  return variant === SweepNotificationVariant.Error
    ? colors.feedback.destructive
    : colors.grey[900];
};

const renderSeverityIcon = (
  severity: SweepNotificationVariant,
  isVariant: boolean,
  color: string,
) => {
  switch (severity) {
    case SweepNotificationVariant.Success:
      return <CheckCircle color={color} />;
    case SweepNotificationVariant.Error:
      return isVariant ? <Notification color={color} /> : <Prohibit color={color} />;
    case SweepNotificationVariant.Warning:
      return <Warning color={color} />;
    case SweepNotificationVariant.Loading:
      return <CenteredCircularProgress size={19} color={color} minHeight="auto" />;
    case SweepNotificationVariant.Default:
    case SweepNotificationVariant.Info:
    default:
      return <Info color={color} />;
  }
};

export const NotificationAlert = forwardRef<any, NotificationAlertProps>(
  (
    {
      message,
      onClose,
      severity = SweepNotificationVariant.Success,
      action,
      details,
      isVariant,
      linkUrl,
      linkText,
      linkOpenInNewTab,
      onNavigate,
      hideClose,
    }: NotificationAlertProps,
    ref,
  ) => {
    const backgroundColor = getBackgroundColor(severity);

    //"variant" doesn't have title and subtitle, so need to combine them.
    const shouldCombine = isVariant && typeof message === 'string' && details;
    const _message = shouldCombine ? `${message}. ${details}` : message;
    const _details = shouldCombine ? undefined : details;

    return (
      <Paper
        ref={ref}
        elevation={0}
        sx={{
          width: 'auto',
          minWidth: '320px',
          maxWidth: '460px',
          borderRadius: '8px',
          padding: '12px',
          background: backgroundColor,
        }}
      >
        <Stack direction="row" gap={1} alignItems="center">
          <Stack direction="row" gap={1} flex={1} alignItems="center">
            <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
              {renderSeverityIcon(severity, isVariant, colors.white)}
            </Box>

            <Typography color={colors.white} variant="caption-bold">
              {_message}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            {linkUrl && (
              <InteractiveButton
                onClick={() => {
                  if (linkOpenInNewTab) {
                    window.open(linkUrl, '_blank', 'noopener,noreferrer');
                  } else {
                    onNavigate?.(linkUrl);
                  }
                }}
              >
                <Typography variant="caption" color={colors.white} textDecoration="underline">
                  {linkText || 'Open'}
                </Typography>
              </InteractiveButton>
            )}

            {action && (
              <InteractiveButton onClick={action.onActionClick}>
                <Typography variant="caption" color={colors.white} textDecoration="underline">
                  {action.actionText}
                </Typography>
              </InteractiveButton>
            )}

            {!hideClose && onClose && (
              <InteractiveButton onClick={onClose} data-testid="notification-close-button">
                <Close color={colors.white} size="small" />
              </InteractiveButton>
            )}
          </Stack>
        </Stack>
        {_details && (
          <Box mt={1} display="flex" alignItems="center">
            <Typography ellipsis whiteSpace="normal" color={colors.white} variant="caption">
              {_details}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  },
);
